import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { ArrowUpDown, MoreHorizontal, ChevronDown, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMainContext } from '@/context/main-context';
import { formatPhoneNumber } from '@/lib/utils';

export default function TeachersDataTable({
  setId,
  data,
  setOpenDelete,
  setOpenEdit,
  children,
}) {
  const history = useNavigate();

  const [sorting, setSorting] = React.useState([]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { loading } = useMainContext();

  const handleRowClick = (teacherId) => {
    history(`/teachers/${teacherId}`);
  };

  const columns = [
    {
      accessorKey: 'fullName',
      header: ({ column }) => (
        <Button
          className="pl-0"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Ism familiya
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div
          title={`${row.getValue('fullName')}ni sahifasiga o'tish`}
          onClick={() => handleRowClick(row.original.id)}
          className="capitalize whitespace-nowrap cursor-pointer"
        >
          {row.getValue('fullName')}
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Telefon raqami',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          {formatPhoneNumber(row.getValue('phone'))}
        </div>
      ),
    },
    {
      accessorKey: 'address',
      header: 'Manzil',
      cell: ({ row }) => (
        <div className="truncate">{row.getValue('address')}</div>
      ),
    },
    {
      accessorKey: 'position',
      header: "Yo'nalish",
      cell: ({ row }) => (
        <div className="whitespace-nowrap">{row.getValue('position')}</div>
      ),
    },
    {
      accessorKey: 'view',
      header: 'Sahifani ochish',
      cell: ({ row }) => (
        <Button
          onClick={() => handleRowClick(row.original.id)}
          variant="ghost"
          className="h-8 w-8 p-0"
        >
          <Eye className="h-4 w-4 cursor-pointer" />
        </Button>
      ),
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const teacher = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                variant="ghost"
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setId(teacher.id);
                  setOpenEdit(true);
                }}
              >
                Tahrirlash
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setId(teacher.id);
                  setOpenDelete(true);
                }}
              >
                O'chirish
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center py-4 gap-2">
          <Input
            placeholder="Ism bilan qidirish..."
            value={table.getColumn('fullName')?.getFilterValue() ?? ''}
            onChange={(event) =>
              table.getColumn('fullName')?.setFilterValue(event.target.value)
            }
            className="w-full md:w-64 xl:w-72"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Ustunlar <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <>{children}</>
      </div>
      <div className="rounded-b-md border max-w-[44rem] min-w-full overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="whitespace-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-16 text-center"
                >
                  {loading ? 'Loading...' : 'No results.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Orqaga
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Oldinga
          </Button>
        </div>
      </div>
    </div>
  );
}
